import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

import {
  useProgramEvaluation,
  type PeDataSource,
} from '../ProgramEvaluation/program-evaluation-hook';
import { hydrateRostersFromUploads, invalidateRosterHydration } from '../Data/uploadedFiles';
import { hydrateSisMappings } from '../Data/sisMappingApi';
import { loadAccessRoster } from '../Upload/studentRoster';
import { buildDashboardData, gatherDashboardSources } from './buildDashboardData';
import { fetchDistrictStats, type DistrictStats } from './districtStatsApi';
import { fetchLicenseRostering, type LicenseRostering } from './licensesApi';
import { useDistrictYears } from '../../core/useDistrictYears';
import { logEvent } from '../../utils/eventLog';
import { currentLoadPhases, loadPhases, recordPhase, setPhase, timedPhase } from '../../utils/loadPhases';
import { fetchBenchmarkMonitoring } from '../Benchmarks/benchmarkMonitoringApi';
import type { BenchmarkMonitoring } from '../Benchmarks/types';
import { DistrictStatsPage } from './components/DistrictStatsPage';
import { Dashboard } from './index';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

type Hydration = 'pending' | 'done' | 'failed';

export type ChecklistSlot = ReactNode | ((stats: DistrictStats | null) => ReactNode);

function sameInstant(held: string | null, named: string): boolean {
  if (held === null) return false;
  const a = Date.parse(held);
  const b = Date.parse(named);
  return !Number.isNaN(a) && !Number.isNaN(b) ? a === b : held === named;
}

const LOAD_TIMING_CAP_MS = 30_000;

export function DashboardLoader({
  source,
  checklist,
  loadTimingCapMs = LOAD_TIMING_CAP_MS,
  loadStartedAt,
  evaluationGeneratedAt = null,
}: {
  source?: PeDataSource;
  checklist?: ChecklistSlot;
  loadTimingCapMs?: number;
  loadStartedAt?: number;
  evaluationGeneratedAt?: string | null;
}) {
  const { report, loading, error, refetch, stale, generatedAt } = useProgramEvaluation(source);
  const [settled, setSettled] = useState(false);
  if (!loading && !settled) setSettled(true);
  const [{ loadStart, mountedAt, phases }] = useState(() => {
    const now = performance.now();
    const recorder = currentLoadPhases();
    if (loadStartedAt !== undefined) setPhase('gate', now - loadStartedAt, recorder);
    return { loadStart: loadStartedAt ?? now, mountedAt: now, phases: recorder };
  });
  const refetchPending = useRef(false);
  useEffect(() => {
    if (loading) return;
    refetchPending.current = false;
    setPhase('evaluation', performance.now() - mountedAt, phases);
  }, [loading, mountedAt, phases]);
  const namedArtifact = useRef<string | null>(null);
  useEffect(() => {
    if (loading || evaluationGeneratedAt === null || evaluationGeneratedAt === undefined || evaluationGeneratedAt === '') return;
    if (namedArtifact.current === evaluationGeneratedAt) return;
    namedArtifact.current = evaluationGeneratedAt;
    if (sameInstant(generatedAt, evaluationGeneratedAt)) return;
    refetchPending.current = true;
    refetch();
  }, [evaluationGeneratedAt, loading, generatedAt, refetch]);
  const { years } = useDistrictYears();
  useEffect(() => {
    if (years) setPhase('years', performance.now() - mountedAt, phases);
  }, [years, mountedAt, phases]);
  const [attempt, setAttempt] = useState(0);
  const [hydration, setHydration] = useState<Hydration>('pending');
  const [licenseRostering, setLicenseRostering] = useState<LicenseRostering | null>(null);
  const [benchmarkMonitoring, setBenchmarkMonitoring] = useState<BenchmarkMonitoring | null>(null);
  const [districtStats, setDistrictStats] = useState<DistrictStats | null>(null);
  const [statsState, setStatsState] = useState<Hydration>('pending');
  const [licensesSettled, setLicensesSettled] = useState(false);
  const [monitoringSettled, setMonitoringSettled] = useState(false);
  useEffect(() => {
    let active = true;
    timedPhase('sisMapping', hydrateSisMappings, phases)
      .then(() =>
        active
          ? timedPhase('hydration', () => hydrateRostersFromUploads(undefined, undefined, { phases }), phases)
          : undefined,
      )
      .then(() => {
        if (active) setHydration('done');
      })
      .catch(() => {
        if (active) setHydration(loadAccessRoster().length > 0 ? 'done' : 'failed');
      });
    return () => {
      active = false;
      invalidateRosterHydration();
    };
  }, [attempt, phases]);
  // The bar is optional, so a slow or failed license request never holds the
  // dashboard or its retry state; the bar appears whenever the request settles.
  useEffect(() => {
    let active = true;
    timedPhase('licenses', fetchLicenseRostering, phases)
      .then((record) => {
        if (active) setLicenseRostering(record);
      })
      .catch((err: unknown) => {
        if (active) setLicenseRostering(null);
        console.warn('licenses unavailable; showing the dashboard without the bar', err);
      })
      .finally(() => {
        if (active) setLicensesSettled(true);
      });
    return () => {
      active = false;
    };
  }, [attempt, phases]);
  // The tile is optional in the same way: a district without Flashlight 360
  // data, or a resolver outage, shows the dashboard without it.
  useEffect(() => {
    let active = true;
    timedPhase('monitoring', () => fetchBenchmarkMonitoring(), phases)
      .then((record) => {
        if (active) setBenchmarkMonitoring(record);
      })
      .catch((err: unknown) => {
        if (active) setBenchmarkMonitoring(null);
        console.warn('benchmark monitoring unavailable; showing the dashboard without the tile', err);
      })
      .finally(() => {
        if (active) setMonitoringSettled(true);
      });
    return () => {
      active = false;
    };
  }, [attempt, phases]);
  // The card is optional the same way: it appears whenever its request settles.
  useEffect(() => {
    let active = true;
    timedPhase('stats', fetchDistrictStats, phases)
      .then((stats) => {
        if (!active) return;
        setDistrictStats(stats);
        setStatsState('done');
      })
      .catch((err: unknown) => {
        if (active) {
          setDistrictStats(null);
          setStatsState('failed');
        }
        console.warn('district stats unavailable; showing the dashboard without the card', err);
      });
    return () => {
      active = false;
    };
  }, [attempt, phases]);
  // Timed from the home page's mount when it says so, until everything the
  // page shows has settled, so the wait a person saw includes the setup
  // spinner and the slowest optional piece, not only the evaluation; an
  // optional request that never settles is capped, since the page is usable
  // without it and a hung session is exactly the one the tab must count.
  const [capElapsed, setCapElapsed] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setCapElapsed(true), loadTimingCapMs);
    return () => clearTimeout(timer);
  }, [loadTimingCapMs]);
  const checklistNode = typeof checklist === 'function' ? checklist(districtStats) : checklist;
  const data = useMemo(() => {
    if (hydration !== 'done' || !years || !report || report.cannot_generate) return null;
    const built = buildDashboardData(gatherDashboardSources(report, stale, years));
    if (!built) return null;
    return {
      ...built,
      ...(licenseRostering ? { licenseRostering } : {}),
      ...(benchmarkMonitoring ? { benchmarkMonitoring } : {}),
      ...(districtStats ? { districtStats } : {}),
    };
  }, [report, hydration, stale, years, licenseRostering, benchmarkMonitoring, districtStats]);
  const loadLogged = useRef(false);
  // The early stand-in (no evaluation) renders on the stats alone; everything
  // else, the late stand-in for an artifact too stale to build included,
  // waits for the roster hydration and the district years first.
  const earlyStandIn = !error && (report == null || report.cannot_generate);
  const hydrated = hydration !== 'pending' && !!years;
  const lateStandIn = !error && !earlyStandIn && hydration === 'done' && !!years && data == null;
  const standInBranch = earlyStandIn || lateStandIn;
  const pageRendered = !loading && (earlyStandIn ? statsState !== 'pending' : hydrated && (!lateStandIn || statsState !== 'pending'));
  const pageFailed = Boolean(error) || (!earlyStandIn && hydration === 'failed') || (standInBranch && statsState === 'failed');
  const optionalSettled = statsState !== 'pending' && licensesSettled && monitoringSettled;
  const pageSettled = pageRendered && (pageFailed || optionalSettled || capElapsed);
  useEffect(() => {
    if (!pageSettled || loadLogged.current || refetchPending.current) return;
    loadLogged.current = true;
    const durationMs = String(Math.max(0, Math.round(performance.now() - loadStart)));
    if (capElapsed && !optionalSettled && !pageFailed) recordPhase('capped', 1, phases);
    const breakdown = loadPhases(phases);
    logEvent('page.loaded.v1', {
      page: 'dashboard',
      durationMs,
      status: pageFailed ? 'error' : 'ok',
      ...(breakdown ? { phases: breakdown } : {}),
    }).catch(() => {});
  }, [pageSettled, pageFailed, loadStart, capElapsed, optionalSettled, phases]);

  const retry = () => {
    setHydration('pending');
    setLicenseRostering(null);
    setBenchmarkMonitoring(null);
    setDistrictStats(null);
    setStatsState('pending');
    setAttempt((n) => n + 1);
    refetch();
  };
  const spinner = (
    <Box
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: HEIGHT_BELOW_BARS }}
    >
      <CircularProgress />
    </Box>
  );
  const statsPage = (
    <DistrictStatsPage
      stats={districtStats}
      licenseRostering={licenseRostering}
      benchmarkMonitoring={benchmarkMonitoring}
      checklist={checklistNode}
      onRetry={statsState === 'failed' ? retry : undefined}
    />
  );
  if (loading && !settled) {
    return spinner;
  }
  // Without a usable evaluation the District Stats card is the page's reason
  // to exist, so its page stands in for the dashboard on its own.
  if (!error && (report == null || report.cannot_generate)) {
    return statsState === 'pending' ? spinner : statsPage;
  }
  if (hydration === 'pending' || !years) {
    return spinner;
  }
  if (error || hydration === 'failed') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          minHeight: HEIGHT_BELOW_BARS,
        }}
      >
        <Typography>We couldn&apos;t load your dashboard.</Typography>
        <Button variant="outlined" onClick={retry}>
          Try again
        </Button>
      </Box>
    );
  }
  if (!data) {
    return statsState === 'pending' ? spinner : statsPage;
  }
  return <Dashboard data={data} checklist={checklistNode} />;
}
